import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Check, Receipt, ArrowClockwise, Lock, ChatCircleDots, FileArrowDown, CalendarBlank, ArrowsClockwise, DownloadSimple } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useRequireAdmin } from '@/hooks/useRequireAdmin';
import { countsLabel, fmtMonth } from '@/utils/dateFmt';
import { fmtDate as fmtDatePt } from '@/utils/dateFmt';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS = {
  pendente: { label: 'Pendente', color: 'bg-brand-bg text-brand-muted' },
  em_validacao: { label: 'Em validação', color: 'bg-amber-50 text-amber-700' },
  corrigido: { label: 'Nota da nutri', color: 'bg-orange-50 text-orange-700' },
  aprovado: { label: 'Aprovado', color: 'bg-blue-50 text-blue-700' },
  recibo_enviado: { label: 'Recibo enviado', color: 'bg-indigo-50 text-indigo-700' },
  pago: { label: 'Pago', color: 'bg-green-50 text-green-700' },
};

function fmt(n) { const num = Number(n) || 0; return num.toLocaleString('pt-PT', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }

function MonthPicker({ value, onChange }) {
  return (
    <div className="flex items-center gap-2">
      <CalendarBlank size={16} className="text-brand-muted" />
      <Input type="month" value={value} onChange={e => onChange(e.target.value)} className="rounded-xl border-brand-border w-44 text-sm" data-testid="month-picker" />
    </div>
  );
}

function PaymentDetailDialog({ payment, onClose, onAfterChange }) {
  const [adminReply, setAdminReply] = useState('');
  const [busy, setBusy] = useState(false);
  if (!payment) return null;
  const items = payment.consultations || [];
  const totalGross = items.reduce((a, b) => a + (Number(b.gross_amount) || 0), 0);
  const totalFee = items.reduce((a, b) => a + (Number(b.stripe_fee) || 0), 0);

  const resend = async () => {
    if (!payment.payment_id) return;
    setBusy(true);
    try { await axios.post(`${API}/api/team/payments/${payment.payment_id}/resend`, { admin_reply: adminReply }); toast.success('Re-enviado'); onAfterChange(); onClose(); } catch (e) { toast.error(e.response?.data?.detail || 'Erro'); }
    finally { setBusy(false); }
  };
  const markPaid = async () => {
    if (!payment.payment_id) return;
    setBusy(true);
    try { await axios.post(`${API}/api/team/payments/${payment.payment_id}/mark-paid`); toast.success('Pagamento marcado como pago'); onAfterChange(); onClose(); } catch (e) { toast.error(e.response?.data?.detail || 'Erro'); }
    finally { setBusy(false); }
  };
  const downloadReceipt = () => {
    window.open(`${API}/api/team/payments/${payment.payment_id}/receipt`, '_blank');
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="rounded-3xl border-brand-border max-h-[90vh] overflow-y-auto" data-testid="payment-detail-dialog">
        <DialogHeader><DialogTitle className="font-heading text-brand-text">{payment.nutri_name} · {fmtMonth(payment.year_month)}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-2 text-xs">
            <div className="bg-brand-bg/40 rounded-2xl p-3"><p className="text-[10px] uppercase text-brand-placeholder">Bruto</p><p className="text-sm font-medium text-brand-text">{fmt(totalGross)} €</p></div>
            <div className="bg-brand-bg/40 rounded-2xl p-3"><p className="text-[10px] uppercase text-brand-placeholder">Taxas Stripe</p><p className="text-sm font-medium text-amber-600">{fmt(totalFee)} €</p></div>
            <div className="bg-brand-green/10 rounded-2xl p-3"><p className="text-[10px] uppercase text-brand-placeholder">A pagar ({payment.percentage}%)</p><p className="text-sm font-semibold text-brand-green">{fmt(payment.payable_amount)} €</p></div>
          </div>

          {payment.nutri_note && (
            <div className="bg-orange-50 border border-orange-200 rounded-2xl p-3" data-testid="payment-nutri-note">
              <p className="text-[10px] uppercase text-orange-700 mb-1">Nota da nutricionista</p>
              <p className="text-xs text-brand-text italic">“{payment.nutri_note}”</p>
            </div>
          )}

          <div>
            <p className="text-[11px] uppercase text-brand-placeholder mb-1">{payment.installments_count ? 'Consultas e prestações' : 'Consultas'} ({items.length})</p>
            <div className="border border-brand-border rounded-2xl overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-brand-bg/40 text-brand-muted">
                  <tr><th className="text-left px-3 py-2">Data</th><th className="text-left px-3 py-2">Paciente</th><th className="text-right px-3 py-2">Bruto</th><th className="text-right px-3 py-2">Taxa</th></tr>
                </thead>
                <tbody>
                  {items.map((c, i) => (
                    <tr key={c.consultation_id || i} className="border-t border-brand-border/40">
                      <td className="px-3 py-2">{fmtDatePt(c.date)}</td>
                      <td className="px-3 py-2 truncate">{c.patient_name || '-'}</td>
                      <td className="px-3 py-2 text-right">{fmt(c.gross_amount)} €</td>
                      <td className="px-3 py-2 text-right text-amber-600">{fmt(c.stripe_fee)} €</td>
                    </tr>
                  ))}
                  {!items.length && <tr><td colSpan="4" className="text-center py-4 text-brand-muted">Sem consultas neste mês.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>

          {payment.status === 'corrigido' && (
            <div className="bg-brand-bg/30 rounded-2xl p-3 space-y-2">
              <Label className="text-[11px] uppercase text-brand-placeholder block">Resposta + correção</Label>
              <Textarea value={adminReply} onChange={e => setAdminReply(e.target.value)} rows={3} placeholder="Ex: ajustei as taxas no separador Consultas. Confere agora." className="rounded-xl border-brand-border text-sm resize-none" data-testid="admin-reply" />
              <button onClick={resend} disabled={busy} className="bg-brand-green text-white rounded-full px-4 py-2 text-xs disabled:opacity-50" data-testid="resend-btn">Re-enviar à nutri</button>
            </div>
          )}

          <div className="flex flex-wrap items-center gap-2 pt-2">
            {payment.receipt_path && (
              <button onClick={downloadReceipt} className="border border-brand-border rounded-full px-3 py-1.5 text-xs flex items-center gap-1 hover:bg-brand-bg" data-testid="download-receipt-btn">
                <FileArrowDown size={14} /> Ver recibo
              </button>
            )}
            {payment.status === 'recibo_enviado' && (
              <button onClick={markPaid} disabled={busy} className="bg-brand-green text-white rounded-full px-4 py-2 text-xs flex items-center gap-1 disabled:opacity-50 ml-auto" data-testid="mark-paid-btn">
                <Check size={14} /> Marcar como pago
              </button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PaymentsTab({ payments, onCloseMonth, onSelect, refreshing }) {
  return (
    <div className="space-y-2" data-testid="admin-payments-tab">
      {payments.length === 0 && <p className="text-center py-10 text-sm text-brand-muted">Sem nutricionistas registadas.</p>}
      {payments.map(p => (
        <div key={p.nutri_id} className="bg-white border border-brand-border rounded-2xl p-4 flex items-center justify-between gap-3 hover:border-brand-green/40 transition-colors" data-testid={`payment-row-${p.nutri_id}`}>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-brand-text">{p.nutri_name}</p>
            <p className="text-[11px] text-brand-muted">{countsLabel(p)} · bruto {fmt(p.gross_total)} € · taxas {fmt(p.stripe_fee_total)} € · <strong className="text-brand-green">a pagar {fmt(p.payable_amount)} €</strong> ({p.percentage}%)</p>
            {p.admin_reply && <p className="text-[10px] text-brand-muted italic mt-1">{p.admin_reply}</p>}
          </div>
          <div className="flex items-center gap-2">
            <span className={`text-[10px] px-2 py-0.5 rounded-full ${STATUS[p.status]?.color || 'bg-brand-bg text-brand-muted'}`}>{STATUS[p.status]?.label || p.status}</span>
            {p.payment_id && (
              <button onClick={() => onSelect(p)} className="text-xs text-brand-green hover:underline" data-testid={`open-payment-${p.nutri_id}`}>Abrir</button>
            )}
            {!p.payment_id || p.status === 'pendente' ? (
              <button onClick={() => onCloseMonth(p)} disabled={refreshing || (!p.consultations_count && !p.payments_count)} className="border border-brand-green text-brand-green rounded-full px-3 py-1.5 text-xs hover:bg-brand-green hover:text-white transition-colors disabled:opacity-30" data-testid={`close-month-${p.nutri_id}`}>
                <Lock size={12} className="inline mr-1" /> Fechar mês
              </button>
            ) : null}
          </div>
        </div>
      ))}
    </div>
  );
}

function HistoryTab({ history }) {
  return (
    <div className="space-y-2" data-testid="admin-history-tab">
      {history.length === 0 && <p className="text-center py-10 text-sm text-brand-muted">Sem histórico ainda.</p>}
      {history.map(h => (
        <div key={h.id} className="bg-white border border-brand-border rounded-2xl p-3 flex items-center gap-3" data-testid={`history-row-${h.id}`}>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-brand-text">{fmtMonth(h.year_month)}</p>
            <p className="text-[11px] text-brand-muted">{countsLabel(h)} · {fmt(h.payable_amount)} €</p>
          </div>
          <span className={`text-[10px] px-2 py-0.5 rounded-full ${STATUS[h.status]?.color || 'bg-brand-bg text-brand-muted'}`}>{STATUS[h.status]?.label || h.status}</span>
        </div>
      ))}
    </div>
  );
}

function TeamMembersTab({ members, onSave }) {
  const [localPct, setLocalPct] = useState({});
  useEffect(() => {
    const init = {};
    members.forEach(m => { init[m.user_id] = m.payment_percentage ?? ''; });
    setLocalPct(init);
  }, [members]);
  const save = async (m) => {
    const v = parseFloat(localPct[m.user_id]);
    if (isNaN(v) || v < 0 || v > 100) { toast.error('Percentagem inválida (0-100)'); return; }
    try { await axios.put(`${API}/api/team/${m.user_id}`, { payment_percentage: v }); toast.success('Percentagem guardada'); onSave(); } catch { toast.error('Erro'); }
  };
  return (
    <div className="space-y-2" data-testid="team-members-tab">
      {members.map(m => (
        <div key={m.user_id} className="bg-white border border-brand-border rounded-2xl p-3 flex items-center gap-3" data-testid={`team-row-${m.user_id}`}>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-brand-text">{m.name}</p>
            <p className="text-[11px] text-brand-muted">{m.email}</p>
          </div>
          <div className="flex items-center gap-1">
            <Input
              type="number" step="1" min="0" max="100"
              value={localPct[m.user_id] ?? ''}
              onChange={e => setLocalPct({ ...localPct, [m.user_id]: e.target.value })}
              className="w-20 text-center rounded-xl border-brand-border text-sm h-9"
              data-testid={`pct-input-${m.user_id}`}
            />
            <span className="text-xs text-brand-muted">%</span>
            <button onClick={() => save(m)} className="ml-2 bg-brand-green text-white rounded-full px-3 py-1.5 text-xs" data-testid={`save-pct-${m.user_id}`}>Guardar</button>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function TeamPaymentsPage() {
  useRequireAdmin();
  const [tab, setTab] = useState('pagamentos');
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [data, setData] = useState({ payments: [] });
  const [history, setHistory] = useState([]);
  const [members, setMembers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      const [pm, hist, team] = await Promise.all([
        axios.get(`${API}/api/team/payments?month=${month}`).catch(() => ({ data: { payments: [] } })),
        axios.get(`${API}/api/team/payments/history`).catch(() => ({ data: [] })),
        axios.get(`${API}/api/team`).catch(() => ({ data: [] })),
      ]);
      setData(pm.data || { payments: [] });
      setHistory(Array.isArray(hist.data) ? hist.data : []);
      setMembers(Array.isArray(team.data) ? team.data : []);
    } catch {}
  }, [month]);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  const closeMonth = async (p) => {
    setBusy(true);
    try { await axios.post(`${API}/api/team/payments/${p.nutri_id}/close-month`, { year_month: month }); toast.success(`Mês fechado para ${p.nutri_name}`); fetchAll(); } catch (e) { toast.error(e.response?.data?.detail || 'Erro'); }
    finally { setBusy(false); }
  };

  const backfill = async () => {
    if (!window.confirm('Cria registos pendentes para todos os meses históricos com consultas?')) return;
    try { const { data } = await axios.post(`${API}/api/team/payments/backfill`); toast.success(`${data.created} registos criados`); fetchAll(); } catch { toast.error('Erro no backfill'); }
  };

  return (
    <div className="fade-in" data-testid="team-payments-page">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div>
          <h2 className="font-heading font-medium text-xl text-brand-text">Pagamentos à Equipa</h2>
          <p className="text-xs text-brand-muted mt-0.5">Resumo, validação, recibos e histórico</p>
        </div>
        <div className="flex items-center gap-2">
          <MonthPicker value={month} onChange={setMonth} />
          <button onClick={fetchAll} className="text-brand-muted hover:text-brand-green p-2" title="Atualizar" data-testid="refresh-btn"><ArrowsClockwise size={16} /></button>
          <button onClick={async () => { try { const res = await axios.get(`${API}/api/team/payments/export?month=${month}`, { responseType: 'blob' }); const url = window.URL.createObjectURL(new Blob([res.data])); const a = document.createElement('a'); a.href = url; a.download = `pagamentos_equipa_${month}.csv`; a.click(); window.URL.revokeObjectURL(url); toast.success('CSV gerado'); } catch { toast.error('Erro a exportar'); } }} className="border border-brand-border text-brand-muted rounded-full px-3 py-1.5 text-xs hover:border-brand-green hover:text-brand-green" data-testid="export-csv-btn"><DownloadSimple size={12} className="inline mr-1" /> CSV</button>
          <button onClick={async () => { if (!window.confirm('Atribuir consultas históricas sem nutricionista usando os appointments do calendário?\\n\\nIsto procura por consultas no mês selecionado sem nutritionist_id e copia o valor do appointment correspondente (mesmo paciente, ±1 dia).')) return; try { const { data } = await axios.post(`${API}/api/admin/consultations/migrate-nutri-from-appointments`, { month }); const total = data.updated_total ?? data.updated ?? 0; const aSrc = data.updated_from_appointment ?? 0; const pSrc = data.updated_from_patient ?? 0; toast.success(`Migradas ${total} (${aSrc} via calendário, ${pSrc} via paciente). ${data.no_match} sem match em ${data.total_scanned} analisadas.`); fetchAll(); } catch (e) { toast.error(e.response?.data?.detail || 'Erro'); } }} className="border border-brand-border text-brand-muted rounded-full px-3 py-1.5 text-xs hover:border-brand-green hover:text-brand-green" data-testid="migrate-nutri-btn" title="Atribuir consultas históricas">Migrar</button>
          <button onClick={async () => { try { const nutriId = window.prompt('user_id da nutricionista a inspecionar (vazio = só totais):', '') || ''; const { data } = await axios.get(`${API}/api/admin/consultations/migration-diagnostic?month=${month}${nutriId ? `&nutri_id=${nutriId}` : ''}`); window.alert(JSON.stringify(data, null, 2)); } catch (e) { toast.error(e.response?.data?.detail || 'Erro'); } }} className="border border-brand-border text-brand-muted rounded-full px-3 py-1.5 text-xs hover:border-brand-green hover:text-brand-green" data-testid="diagnostic-btn" title="Ver dados crus">Diagnóstico</button>
          <button onClick={backfill} className="border border-brand-border text-brand-muted rounded-full px-3 py-1.5 text-xs hover:border-brand-green hover:text-brand-green" data-testid="backfill-btn"><ArrowClockwise size={12} className="inline mr-1" /> Backfill</button>
        </div>
      </div>

      <div className="flex gap-2 mb-5">
        <button onClick={() => setTab('pagamentos')} className={`flex-1 py-2.5 rounded-xl text-sm font-medium ${tab === 'pagamentos' ? 'bg-brand-green text-white' : 'bg-brand-bg text-brand-muted border border-brand-border'}`} data-testid="tab-pagamentos">Mês atual</button>
        <button onClick={() => setTab('historico')} className={`flex-1 py-2.5 rounded-xl text-sm font-medium ${tab === 'historico' ? 'bg-brand-green text-white' : 'bg-brand-bg text-brand-muted border border-brand-border'}`} data-testid="tab-historico">Histórico</button>
        <button onClick={() => setTab('equipa')} className={`flex-1 py-2.5 rounded-xl text-sm font-medium ${tab === 'equipa' ? 'bg-brand-green text-white' : 'bg-brand-bg text-brand-muted border border-brand-border'}`} data-testid="tab-equipa">Equipa</button>
      </div>

      {tab === 'pagamentos' && <PaymentsTab payments={data.payments || []} onCloseMonth={closeMonth} onSelect={setSelected} refreshing={busy} />}
      {tab === 'historico' && <HistoryTab history={history} />}
      {tab === 'equipa' && <TeamMembersTab members={members} onSave={fetchAll} />}

      <PaymentDetailDialog payment={selected} onClose={() => setSelected(null)} onAfterChange={fetchAll} />
    </div>
  );
}
